import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import '../App.css';

// Initialize Stripe with publishable key (you'll need to add this to your .env.local)
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

function PaymentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  
  // Get quiz answers from location state to pass to checkout
  // We only store answers (12 numbers) to stay under Stripe's 500 char metadata limit
  const quizAnswers = location.state?.answers;
  
  // Get imgbb URL from sessionStorage (uploaded when image was generated)
  const tarotCardUrl = sessionStorage.getItem('money_mirror_imgbb_url');

  // Track checkout page view in PostHog
  useEffect(() => {
    if (window.posthog) {
      window.posthog.capture('checkout_viewed');
    }
  }, []);

  useEffect(() => {
    // Get payment link or price ID from environment
    const paymentConfig = process.env.REACT_APP_STRIPE_PRICE_ID;
    const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    
    // Debug logging
    console.log('Payment config from env:', paymentConfig);
    console.log('Publishable key set:', !!publishableKey);
    console.log('Location state:', { hasAnswers: !!quizAnswers, hasTarotCardUrl: !!tarotCardUrl });
    
    // Check if Stripe is initialized
    if (!publishableKey) {
      setError('Stripe publishable key not configured. Please contact support.');
      setIsLoading(false);
      return;
    }
    
    // Check if it's a Payment Link URL (starts with http), Price ID (starts with price_), or Product ID (starts with prod_)
    if (paymentConfig && paymentConfig.startsWith('http')) {
      // It's a Payment Link URL - redirect directly to Stripe
      window.location.href = paymentConfig;
    } else if (paymentConfig && (paymentConfig.startsWith('price_') || paymentConfig.startsWith('prod_'))) {
      // It's a Price ID or Product ID - create Embedded Checkout Session via API
      const createCheckoutSession = async () => {
        try {
          const origin = window.location.origin;
          const requestBody = paymentConfig.startsWith('prod_')
            ? { productId: paymentConfig }
            : { priceId: paymentConfig };
          
          console.log('Creating embedded checkout session with:', {
            url: '/api/create-checkout-session',
            body: requestBody,
            origin
          });
          
          const response = await fetch('/api/create-checkout-session', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              ...requestBody,
              embedded: true, // Request embedded checkout
              successUrl: `${origin}/payment-success`,
              cancelUrl: `${origin}/results`,
              // Include quiz answers for PDF generation (webhook will recalculate results)
              ...(quizAnswers && { quizAnswers }),
              // Include tarot card URL for email PDF (avoids regenerating)
              ...(tarotCardUrl && { tarotCardUrl }),
            }),
          });

          console.log('Response status:', response.status, response.statusText);

          // Check if response is ok and has content
          if (!response.ok) {
            const text = await response.text();
            console.error('Error response text:', text);
            let errorData;
            try {
              errorData = JSON.parse(text);
            } catch (e) {
              throw new Error(`Server error: ${response.status} ${response.statusText}. Response: ${text.substring(0, 200)}`);
            }
            throw new Error(errorData.error || 'Failed to create checkout session');
          }

          // Parse JSON response
          const text = await response.text();
          
          if (!text || text.trim() === '') {
            throw new Error('Empty response from server');
          }
          
          let data;
          try {
            data = JSON.parse(text);
            console.log('Parsed data:', { sessionId: data.sessionId, hasClientSecret: !!data.clientSecret });
          } catch (e) {
            console.error('JSON parse error:', e);
            console.error('Response text:', text);
            throw new Error(`Invalid JSON response: ${e.message}. Response preview: ${text.substring(0, 200)}`);
          }

          // Check for error in response even if status is 200
          if (data.error) {
            throw new Error(data.error);
          }

          // Set client secret for embedded checkout
          if (data.clientSecret) {
            setClientSecret(data.clientSecret);
            setIsLoading(false);
          } else {
            // Log the full response to help debug
            console.error('Response data:', data);
            throw new Error(`No client secret received in response. Response: ${JSON.stringify(data)}`);
          }
        } catch (err) {
          console.error('Error creating checkout session:', err);
          console.error('Error stack:', err.stack);
          setError(err.message || 'Failed to start payment process');
          setIsLoading(false);
        }
      };

      createCheckoutSession();
    } else {
      console.error('Payment configuration not found. REACT_APP_STRIPE_PRICE_ID:', paymentConfig);
      setError('Payment configuration not found. Please set REACT_APP_STRIPE_PRICE_ID in your environment variables.');
      setIsLoading(false);
    }
  }, [quizAnswers]);

  const options = { clientSecret };

  if (error) {
    return (
      <div className="app-container">
        <div className="blue-gradient-overlay"></div>
        <div className="main-content results-content">
          <div className="title-section">
            <p className="title-line-1">Payment Error</p>
            <p className="title-line-2">{error}</p>
          </div>
          <button 
            className="primary-button" 
            onClick={() => navigate('/results')}
            style={{ marginTop: '40px' }}
          >
            Return to Results
          </button>
        </div>
      </div>
    );
  }

  if (isLoading || !clientSecret) {
    return (
      <div className="app-container">
        <div className="blue-gradient-overlay"></div>
        <div className="main-content" style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          padding: 0
        }}>
          <p className="loading-text">
            Loading<span className="loading-dots">
              <span>.</span><span>.</span><span>.</span>
            </span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <div className="blue-gradient-overlay"></div>
      <div className="main-content results-content" style={{ paddingTop: '40px' }}>
        <div className="title-section">
          <p className="title-line-1">UNLOCK</p>
          <p className="title-line-2">Your Report</p>
        </div>
        <div className="payment-description">
          <p className="payment-description-text">
            Get the complete 10-page personalised report emailed to you. It will contain detailed insights into your money beliefs, 
            and actionable guidance for the year ahead, all in a downloadable PDF you can keep forever.
          </p>
        </div>
        <div className="payment-checkout-wrapper">
          <div className="payment-checkout-container">
            <EmbeddedCheckoutProvider
              stripe={stripePromise}
              options={options}
            >
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentPage;
